import { useState, useEffect } from 'react';
import { API, getAuthHeader } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Save, RotateCcw, Plus, Trash2, MessageSquare, ChevronDown, ChevronRight, 
  Loader2, ArrowRight, Settings, Layers, Edit2, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// Componente para sub-opção (nível 2) - não recursivo
function SubOpcaoItem({ numero, opcao, onUpdate, onDelete, expanded, onToggleExpand, parentNum }) {
  var path = parentNum + '.' + numero;
  var isExpanded = expanded[path] !== false;
  
  return (
    <div className="rounded-lg border-2 border-purple-200 overflow-hidden ml-6 mb-2">
      <div 
        className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:opacity-90 bg-purple-50"
        onClick={function() { onToggleExpand(path); }}
      >
        <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
          {numero}
        </div>
        <div className="flex-1 min-w-0">
          <span className="font-medium text-slate-700 text-sm">{opcao.texto || 'Sem título'}</span>
        </div>
        {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0"
          onClick={function(e) { e.stopPropagation(); onDelete(numero); }}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
      
      {isExpanded && (
        <div className="p-3 bg-white border-t space-y-2">
          <div>
            <label className="text-xs font-medium text-slate-500">Texto do Menu</label>
            <Input 
              value={opcao.texto || ''}
              onChange={function(e) { onUpdate(numero, 'texto', e.target.value); }}
              placeholder="Ex: Internet lenta"
              className="text-sm h-8"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Resposta</label>
            <textarea 
              className="w-full border rounded-md p-2 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-purple-500 resize-y"
              value={opcao.resposta || ''}
              onChange={function(e) { onUpdate(numero, 'resposta', e.target.value); }}
              placeholder="Resposta automática..."
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Componente para opção principal (nível 1)
function OpcaoPrincipalItem({ numero, opcao, onUpdate, onDelete, onAddSubmenu, onUpdateSubmenu, onDeleteSubmenu, categorias, expanded, onToggleExpand }) {
  var path = numero;
  var isExpanded = expanded[path] !== false;
  var temSubmenu = opcao.submenu && Object.keys(opcao.submenu).length > 0;
  
  return (
    <div className="rounded-xl border-2 border-blue-200 overflow-hidden">
      <div 
        className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:opacity-90 bg-blue-50"
        onClick={function() { onToggleExpand(path); }}
      >
        <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
          {numero}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800">{opcao.texto || 'Sem título'}</span>
            {opcao.categoria && (
              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                {opcao.categoria}
              </span>
            )}
            {temSubmenu && (
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                <Layers className="w-3 h-3 inline mr-1" />
                {Object.keys(opcao.submenu).length} sub-opções
              </span>
            )}
          </div>
          {opcao.resposta && (
            <p className="text-xs text-slate-500 truncate mt-0.5">
              {opcao.resposta.substring(0, 60)}...
            </p>
          )}
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
          onClick={function(e) { e.stopPropagation(); onDelete(numero); }}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
        {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
      </div>
      
      {isExpanded && (
        <div className="p-4 bg-white border-t space-y-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 pt-1">
              <label className="text-xs font-medium text-slate-500 block mb-1">Opção</label>
              <div className="w-12 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-lg">
                {numero}
              </div>
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-500 block mb-1">Texto do Menu</label>
              <Input 
                value={opcao.texto || ''}
                onChange={function(e) { onUpdate(numero, 'texto', e.target.value); }}
                placeholder="Ex: Quero ser Cliente"
                className="font-medium"
              />
            </div>
            <div className="w-36 flex-shrink-0">
              <label className="text-xs font-medium text-slate-500 block mb-1">Categoria</label>
              <select 
                className="w-full border rounded-md p-2 text-sm bg-white h-10"
                value={opcao.categoria || 'outros'}
                onChange={function(e) { onUpdate(numero, 'categoria', e.target.value); }}
              >
                {categorias.map(function(cat) {
                  return <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>;
                })}
              </select>
            </div>
          </div>
          
          <div>
            <label className="text-xs font-medium text-slate-500 block mb-1">
              <ArrowRight className="w-3 h-3 inline mr-1" />
              Resposta
            </label>
            <textarea 
              className="w-full border rounded-lg p-3 text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
              value={opcao.resposta || ''}
              onChange={function(e) { onUpdate(numero, 'resposta', e.target.value); }}
              placeholder="Resposta automática para esta opção..."
            />
          </div>
          
          <div className="flex items-center gap-4 pt-2 border-t">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
              <input 
                type="checkbox" 
                checked={opcao.auto_boleto || false}
                onChange={function(e) { onUpdate(numero, 'auto_boleto', e.target.checked); }}
                className="w-4 h-4 rounded border-slate-300 text-blue-600"
              />
              Buscar boleto automaticamente
            </label>
          </div>
          
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-purple-600" />
                <span className="font-semibold text-slate-700">Sub-opções (menu de segundo nível)</span>
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={function() { onAddSubmenu(numero); }}
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
              >
                <Plus className="w-3 h-3 mr-1" /> Adicionar
              </Button>
            </div>
            
            {temSubmenu ? (
              <div className="space-y-2">
                {Object.keys(opcao.submenu).sort(function(a, b) { 
                  if (a === '0') return 1;
                  if (b === '0') return -1;
                  return parseInt(a) - parseInt(b); 
                }).map(function(subNum) {
                  return (
                    <SubOpcaoItem 
                      key={subNum}
                      numero={subNum}
                      opcao={opcao.submenu[subNum]}
                      onUpdate={function(n, campo, valor) { onUpdateSubmenu(numero, n, campo, valor); }}
                      onDelete={function(n) { onDeleteSubmenu(numero, n); }}
                      expanded={expanded}
                      onToggleExpand={onToggleExpand}
                      parentNum={numero}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-500">Nenhuma sub-opção configurada.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ConfigMensagensPage() {
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];
  
  var menuState = useState({});
  var menu = menuState[0];
  var setMenu = menuState[1];
  
  var mensagensState = useState({});
  var mensagens = mensagensState[0];
  var setMensagens = mensagensState[1];
  
  var categoriasState = useState([]);
  var categorias = categoriasState[0];
  var setCategorias = categoriasState[1];

  var activeTabState = useState('menu');
  var activeTab = activeTabState[0];
  var setActiveTab = activeTabState[1];

  var expandedState = useState({});
  var expanded = expandedState[0];
  var setExpanded = expandedState[1];

  // Permissões do usuário
  var currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  var isSuperAdmin = currentUser.is_super_admin === true;
  var permissoes = currentUser.permissoes || {};
  var podeVer = isSuperAdmin || permissoes.construtor_menu;
  var podeCriar = isSuperAdmin || permissoes.construtor_menu_criar;
  var podeEditar = isSuperAdmin || permissoes.construtor_menu_editar;
  var podeExcluir = isSuperAdmin || permissoes.construtor_menu_excluir;

  // Respostas Rápidas
  var respostasRapidasState = useState([]);
  var respostasRapidas = respostasRapidasState[0];
  var setRespostasRapidas = respostasRapidasState[1];
  var novaRespostaState = useState({ titulo: '', texto: '', categoria: 'geral' });
  var novaResposta = novaRespostaState[0];
  var setNovaResposta = novaRespostaState[1];
  var editandoRespostaState = useState(null);
  var editandoResposta = editandoRespostaState[0];
  var setEditandoResposta = editandoRespostaState[1];

  // Planos do Atendimento
  var planosFibraState = useState([]);
  var planosFibra = planosFibraState[0];
  var setPlanosFibra = planosFibraState[1];
  var planosMovelState = useState([]);
  var planosMovel = planosMovelState[0];
  var setPlanosMovel = planosMovelState[1];
  var planosTvState = useState([]);
  var planosTv = planosTvState[0];
  var setPlanosTv = planosTvState[1];
  var salvandoPlanosState = useState(false);
  var salvandoPlanos = salvandoPlanosState[0];
  var setSalvandoPlanos = salvandoPlanosState[1];

  useEffect(function() {
    loadConfig();
    loadRespostasRapidas();
    loadPlanos();
  }, []);

  function loadPlanos() {
    axios.get(API + '/config/planos-atendimento', { headers: getAuthHeader() })
      .then(function(res) {
        setPlanosFibra(res.data.fibra || []);
        setPlanosMovel(res.data.movel || []);
        setPlanosTv(res.data.tv || []);
      })
      .catch(function() {
        toast.error('Erro ao carregar planos');
      });
  }

  function salvarPlanos() {
    setSalvandoPlanos(true);
    axios.put(API + '/config/planos-atendimento', {
      fibra: planosFibra,
      movel: planosMovel,
      tv: planosTv
    }, { headers: getAuthHeader() })
      .then(function() {
        toast.success('Planos salvos com sucesso!');
      })
      .catch(function() {
        toast.error('Erro ao salvar planos');
      })
      .finally(function() {
        setSalvandoPlanos(false);
      });
  }

  function adicionarPlano(tipo) {
    var novoId = String(Date.now());
    var novoPlano = { id: novoId, nome: '', valor: '' };
    if (tipo === 'fibra') {
      setPlanosFibra([...planosFibra, novoPlano]);
    } else if (tipo === 'movel') {
      setPlanosMovel([...planosMovel, novoPlano]);
    } else if (tipo === 'tv') {
      setPlanosTv([...planosTv, novoPlano]);
    }
  }

  function removerPlano(tipo, id) {
    if (tipo === 'fibra') {
      setPlanosFibra(planosFibra.filter(function(p) { return p.id !== id; }));
    } else if (tipo === 'movel') {
      setPlanosMovel(planosMovel.filter(function(p) { return p.id !== id; }));
    } else if (tipo === 'tv') {
      setPlanosTv(planosTv.filter(function(p) { return p.id !== id; }));
    }
  }

  function atualizarPlano(tipo, id, campo, valor) {
    var updateFn = function(planos) {
      return planos.map(function(p) {
        if (p.id === id) {
          var updated = {...p};
          updated[campo] = valor;
          return updated;
        }
        return p;
      });
    };
    if (tipo === 'fibra') {
      setPlanosFibra(updateFn(planosFibra));
    } else if (tipo === 'movel') {
      setPlanosMovel(updateFn(planosMovel));
    } else if (tipo === 'tv') {
      setPlanosTv(updateFn(planosTv));
    }
  }

  function loadRespostasRapidas() {
    axios.get(API + '/atendimentos/respostas-rapidas', { headers: getAuthHeader() })
      .then(function(res) {
        // O endpoint retorna { respostas: [...] }, então pegamos a propriedade correta
        var data = res.data;
        if (Array.isArray(data)) {
          setRespostasRapidas(data);
        } else if (data && Array.isArray(data.respostas)) {
          setRespostasRapidas(data.respostas);
        } else {
          setRespostasRapidas([]);
        }
      })
      .catch(function() {
        setRespostasRapidas([]);
      });
  }

  function criarRespostaRapida() {
    if (!novaResposta.titulo || !novaResposta.texto) {
      toast.error('Preencha o título e o texto da resposta');
      return;
    }
    axios.post(API + '/atendimentos/respostas-rapidas', novaResposta, { headers: getAuthHeader() })
      .then(function() {
        toast.success('Resposta rápida criada!');
        setNovaResposta({ titulo: '', texto: '', categoria: 'geral' });
        loadRespostasRapidas();
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Erro ao criar resposta');
      });
  }

  function excluirRespostaRapida(id) {
    if (!confirm('Excluir esta resposta rápida?')) return;
    axios.delete(API + '/atendimentos/respostas-rapidas/' + id, { headers: getAuthHeader() })
      .then(function() {
        toast.success('Resposta excluída');
        loadRespostasRapidas();
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Erro ao excluir');
      });
  }

  function iniciarEdicaoResposta(resp) {
    setEditandoResposta(resp);
    setNovaResposta({
      titulo: resp.titulo,
      texto: resp.texto,
      categoria: resp.categoria || 'geral'
    });
  }

  function cancelarEdicao() {
    setEditandoResposta(null);
    setNovaResposta({ titulo: '', texto: '', categoria: 'geral' });
  }

  function salvarEdicaoResposta() {
    if (!novaResposta.titulo || !novaResposta.texto) {
      toast.error('Preencha o título e o texto da resposta');
      return;
    }
    axios.put(API + '/atendimentos/respostas-rapidas/' + editandoResposta.id, novaResposta, { headers: getAuthHeader() })
      .then(function() {
        toast.success('Resposta atualizada!');
        setEditandoResposta(null);
        setNovaResposta({ titulo: '', texto: '', categoria: 'geral' });
        loadRespostasRapidas();
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Erro ao atualizar resposta');
      });
  }

  function loadConfig() {
    setLoading(true);
    axios.get(API + '/config/mensagens', { headers: getAuthHeader() })
      .then(function(res) {
        setMenu(res.data.menu || {});
        setMensagens(res.data.mensagens || {});
        setCategorias(res.data.categorias_disponiveis || ['suporte', 'financeiro', 'comercial', 'cancelamento', 'atendente', 'outros']);
        setLoading(false);
      })
      .catch(function() {
        toast.error('Erro ao carregar configurações');
        setLoading(false);
      });
  }

  function saveMenu() {
    setSaving(true);
    axios.put(API + '/config/mensagens/menu', menu, { headers: getAuthHeader() })
      .then(function() {
        toast.success('Menu salvo com sucesso!');
        setSaving(false);
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Erro ao salvar menu');
        setSaving(false);
      });
  }

  function saveMensagens() {
    setSaving(true);
    axios.put(API + '/config/mensagens/textos', mensagens, { headers: getAuthHeader() })
      .then(function() {
        toast.success('Mensagens salvas com sucesso!');
        setSaving(false);
      })
      .catch(function(err) {
        toast.error(err.response?.data?.detail || 'Erro ao salvar mensagens');
        setSaving(false);
      });
  }

  function resetConfig() {
    if (!window.confirm('Deseja realmente resetar todas as configurações para os valores padrão?')) return;
    setSaving(true);
    axios.post(API + '/config/mensagens/reset', {}, { headers: getAuthHeader() })
      .then(function(res) {
        setMenu(res.data.menu || {});
        setMensagens(res.data.mensagens || {});
        toast.success('Configurações resetadas!');
        setSaving(false);
      })
      .catch(function() {
        toast.error('Erro ao resetar');
        setSaving(false);
      });
  }

  function toggleExpanded(path) {
    var newExp = {};
    Object.keys(expanded).forEach(function(k) { newExp[k] = expanded[k]; });
    newExp[path] = expanded[path] === false ? true : false;
    setExpanded(newExp);
  }

  function updateOpcao(numero, campo, valor) {
    var novoMenu = JSON.parse(JSON.stringify(menu));
    novoMenu[numero][campo] = valor;
    setMenu(novoMenu);
  }

  function updateSubmenu(numero, subNumero, campo, valor) {
    var novoMenu = JSON.parse(JSON.stringify(menu));
    if (!novoMenu[numero].submenu) novoMenu[numero].submenu = {};
    if (!novoMenu[numero].submenu[subNumero]) novoMenu[numero].submenu[subNumero] = {};
    novoMenu[numero].submenu[subNumero][campo] = valor;
    setMenu(novoMenu);
  }

  function addSubmenu(numero) {
    var novoMenu = JSON.parse(JSON.stringify(menu));
    if (!novoMenu[numero].submenu) novoMenu[numero].submenu = {};
    var subNums = Object.keys(novoMenu[numero].submenu).map(function(k) { return parseInt(k); }).filter(function(n) { return n > 0; });
    var prox = subNums.length > 0 ? Math.max.apply(null, subNums) + 1 : 1;
    novoMenu[numero].submenu[prox] = { texto: 'Nova opção', resposta: '' };
    setMenu(novoMenu);
    toast.success('Sub-opção adicionada!');
  }

  function deleteSubmenu(numero, subNumero) {
    if (!window.confirm('Excluir esta sub-opção?')) return;
    var novoMenu = JSON.parse(JSON.stringify(menu));
    delete novoMenu[numero].submenu[subNumero];
    setMenu(novoMenu);
    toast.success('Sub-opção excluída!');
  }

  function deleteOpcao(numero) {
    if (!window.confirm('Excluir a opção ' + numero + '?')) return;
    var novoMenu = JSON.parse(JSON.stringify(menu));
    delete novoMenu[numero];
    setMenu(novoMenu);
    toast.success('Opção excluída!');
  }

  function addOpcao() {
    var nums = Object.keys(menu).map(function(k) { return parseInt(k); });
    var prox = nums.length > 0 ? Math.max.apply(null, nums) + 1 : 1;
    var novoMenu = JSON.parse(JSON.stringify(menu));
    novoMenu[prox] = { texto: 'Nova Opção', resposta: 'Resposta automática.', categoria: 'outros', auto_boleto: false };
    setMenu(novoMenu);
    toast.success('Opção adicionada!');
  }

  function updateMensagem(chave, valor) {
    var novas = {};
    Object.keys(mensagens).forEach(function(k) { novas[k] = mensagens[k]; });
    novas[chave] = valor;
    setMensagens(novas);
  }

  function expandirTodos() {
    var newExp = {};
    Object.keys(menu).forEach(function(num) {
      newExp[num] = true;
      if (menu[num].submenu) {
        Object.keys(menu[num].submenu).forEach(function(subNum) {
          newExp[num + '.' + subNum] = true;
        });
      }
    });
    setExpanded(newExp);
  }

  function recolherTodos() {
    var newExp = {};
    Object.keys(menu).forEach(function(num) { newExp[num] = false; });
    setExpanded(newExp);
  }

  var numerosOrdenados = Object.keys(menu).sort(function(a, b) { return parseInt(a) - parseInt(b); });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Settings className="w-7 h-7 text-blue-600" />
            Construtor de Menus
          </h1>
          <p className="text-slate-500 mt-1">Configure as opções e respostas do menu de atendimento em árvore hierárquica</p>
        </div>
        <Button variant="outline" onClick={resetConfig} disabled={saving} className="text-slate-600">
          <RotateCcw className="w-4 h-4 mr-2" />
          Resetar
        </Button>
      </div>

      <div className="flex gap-2 mb-6 border-b overflow-x-auto pb-1 scrollbar-hide">
        <button
          className={'px-3 py-2 font-medium border-b-2 transition-colors whitespace-nowrap text-sm ' + (activeTab === 'menu' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700')}
          onClick={function() { setActiveTab('menu'); }}
        >
          <MessageSquare className="w-4 h-4 inline mr-1" />
          Menu
        </button>
        <button
          className={'px-3 py-2 font-medium border-b-2 transition-colors whitespace-nowrap text-sm ' + (activeTab === 'mensagens' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700')}
          onClick={function() { setActiveTab('mensagens'); }}
        >
          Mensagens
        </button>
        <button
          className={'px-3 py-2 font-medium border-b-2 transition-colors whitespace-nowrap text-sm ' + (activeTab === 'respostas' ? 'border-green-600 text-green-600' : 'border-transparent text-slate-500 hover:text-slate-700')}
          onClick={function() { setActiveTab('respostas'); }}
        >
          Respostas
        </button>
        <button
          className={'px-3 py-2 font-medium border-b-2 transition-colors whitespace-nowrap text-sm ' + (activeTab === 'planos' ? 'border-orange-600 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-700')}
          onClick={function() { setActiveTab('planos'); }}
        >
          Planos
        </button>
      </div>

      {activeTab === 'menu' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={expandirTodos}>
                <ChevronDown className="w-4 h-4 mr-1" /> Expandir Todos
              </Button>
              <Button size="sm" variant="outline" onClick={recolherTodos}>
                <ChevronRight className="w-4 h-4 mr-1" /> Recolher Todos
              </Button>
            </div>
            <Button size="sm" onClick={addOpcao} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-1" /> Nova Opção Principal
            </Button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <strong>Como funciona:</strong> Cada opção pode ter sub-opções. Clique para expandir e editar.
          </div>

          <div className="space-y-4">
            {numerosOrdenados.map(function(numero) {
              return (
                <OpcaoPrincipalItem 
                  key={numero}
                  numero={numero}
                  opcao={menu[numero]}
                  onUpdate={updateOpcao}
                  onDelete={deleteOpcao}
                  onAddSubmenu={addSubmenu}
                  onUpdateSubmenu={updateSubmenu}
                  onDeleteSubmenu={deleteSubmenu}
                  categorias={categorias}
                  expanded={expanded}
                  onToggleExpand={toggleExpanded}
                />
              );
            })}
          </div>
          
          {numerosOrdenados.length === 0 && (
            <div className="p-8 text-center text-slate-500 bg-white rounded-xl border-2 border-dashed">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Nenhuma opção configurada</p>
            </div>
          )}
          
          <div className="flex justify-end pt-4 sticky bottom-4">
            <Button 
              onClick={saveMenu} 
              disabled={saving || !podeEditar} 
              className="bg-blue-600 hover:bg-blue-700 px-8 shadow-lg disabled:opacity-50" 
              size="lg"
              title={!podeEditar ? 'Você não tem permissão para editar' : ''}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar Alterações
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'mensagens' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b">
              <span className="font-medium text-slate-700">Mensagens Automáticas do Sistema</span>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase">Saudação (Início do Atendimento)</label>
                <textarea 
                  className="w-full border rounded-md p-3 text-sm mt-1 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  value={mensagens.saudacao_base || ''}
                  onChange={function(e) { updateMensagem('saudacao_base', e.target.value); }}
                  placeholder="Ex: Olá! Bem-vindo ao atendimento HOME ON. Como posso ajudar?"
                />
                <p className="text-xs text-slate-400 mt-1">Esta mensagem aparece antes do menu de opções quando o cliente inicia uma conversa</p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase">Opção Inválida</label>
                <textarea 
                  className="w-full border rounded-md p-3 text-sm mt-1 min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  value={mensagens.opcao_invalida || ''}
                  onChange={function(e) { updateMensagem('opcao_invalida', e.target.value); }}
                  placeholder="Mensagem quando o cliente digita uma opção inválida"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase">Finalização (Sistema)</label>
                <textarea 
                  className="w-full border rounded-md p-3 text-sm mt-1 min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  value={mensagens.finalizacao || ''}
                  onChange={function(e) { updateMensagem('finalizacao', e.target.value); }}
                  placeholder="Mensagem exibida no sistema quando o atendimento é finalizado"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase">Encerramento (Operador)</label>
                <textarea 
                  className="w-full border rounded-md p-3 text-sm mt-1 min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  value={mensagens.encerramento_operador || ''}
                  onChange={function(e) { updateMensagem('encerramento_operador', e.target.value); }}
                  placeholder="Mensagem interna quando operador finaliza (Ex: Atendimento finalizado. Obrigado!)"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase">Encerramento (WhatsApp)</label>
                <textarea 
                  className="w-full border rounded-md p-3 text-sm mt-1 min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  value={mensagens.encerramento_whatsapp || ''}
                  onChange={function(e) { updateMensagem('encerramento_whatsapp', e.target.value); }}
                  placeholder="Mensagem enviada ao cliente no WhatsApp quando o atendimento é finalizado"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase">Aguarde Atendente</label>
                <textarea 
                  className="w-full border rounded-md p-3 text-sm mt-1 min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  value={mensagens.aguarde_atendente || ''}
                  onChange={function(e) { updateMensagem('aguarde_atendente', e.target.value); }}
                  placeholder="Mensagem enviada quando o cliente aguarda um atendente"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase">Fora do Horário</label>
                <textarea 
                  className="w-full border rounded-md p-3 text-sm mt-1 min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  value={mensagens.fora_horario || ''}
                  onChange={function(e) { updateMensagem('fora_horario', e.target.value); }}
                  placeholder="Mensagem enviada fora do horário de atendimento"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <Button 
              onClick={saveMensagens} 
              disabled={saving || !podeEditar} 
              className="bg-blue-600 hover:bg-blue-700 px-8 disabled:opacity-50" 
              size="lg"
              title={!podeEditar ? 'Você não tem permissão para editar' : ''}
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar Mensagens
            </Button>
          </div>
        </div>
      )}

      {/* Aba Respostas Rápidas */}
      {activeTab === 'respostas' && (
        <div className="space-y-6">
          {/* Formulário para criar/editar resposta */}
          <div className={'bg-white rounded-xl shadow-sm border overflow-hidden ' + (editandoResposta ? 'ring-2 ring-blue-500' : '')}>
            <div className={(editandoResposta ? 'bg-blue-50' : 'bg-green-50') + ' px-4 py-3 border-b flex items-center justify-between'}>
              <span className={'font-medium ' + (editandoResposta ? 'text-blue-700' : 'text-green-700')}>
                {editandoResposta ? '✏️ Editando Resposta Rápida' : '➕ Nova Resposta Rápida'}
              </span>
              {editandoResposta && (
                <Button variant="ghost" size="sm" onClick={cancelarEdicao} className="text-blue-600 hover:text-blue-800">
                  <X className="w-4 h-4 mr-1" /> Cancelar Edição
                </Button>
              )}
            </div>
            <div className="p-4 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Título / Atalho *</label>
                  <Input 
                    placeholder="Ex: Saudação inicial"
                    value={novaResposta.titulo}
                    onChange={function(e) { setNovaResposta({...novaResposta, titulo: e.target.value}); }}
                    className={editandoResposta ? 'border-blue-300 focus:ring-blue-500' : ''}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Categoria</label>
                  <select 
                    className="w-full border rounded-md p-2 text-sm"
                    value={novaResposta.categoria}
                    onChange={function(e) { setNovaResposta({...novaResposta, categoria: e.target.value}); }}
                  >
                    <option value="geral">Geral</option>
                    <option value="saudacao">Saudação</option>
                    <option value="suporte">Suporte</option>
                    <option value="financeiro">Financeiro</option>
                    <option value="comercial">Comercial</option>
                    <option value="encerramento">Encerramento</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Texto da Resposta *</label>
                <textarea 
                  className={'w-full border rounded-md p-3 text-sm mt-1 min-h-[80px] focus:outline-none focus:ring-2 resize-y ' + 
                    (editandoResposta ? 'focus:ring-blue-500 border-blue-300' : 'focus:ring-green-500')}
                  placeholder="Digite o texto da resposta que será enviada ao cliente..."
                  value={novaResposta.texto}
                  onChange={function(e) { setNovaResposta({...novaResposta, texto: e.target.value}); }}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button 
                  onClick={editandoResposta ? salvarEdicaoResposta : criarRespostaRapida} 
                  className={editandoResposta ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"}
                  disabled={editandoResposta ? !podeEditar : !podeCriar}
                  title={editandoResposta ? (!podeEditar ? 'Sem permissão para editar' : '') : (!podeCriar ? 'Sem permissão para criar' : '')}
                >
                  {editandoResposta ? (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Salvar Alterações
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Criar Resposta
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Lista de respostas existentes */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b flex items-center justify-between">
              <span className="font-medium text-slate-700">Respostas Cadastradas ({respostasRapidas.length})</span>
            </div>
            <div className="divide-y">
              {respostasRapidas.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma resposta rápida cadastrada</p>
                  <p className="text-sm">Crie sua primeira resposta acima</p>
                </div>
              ) : (
                respostasRapidas.map(function(resp) {
                  return (
                    <div key={resp.id} className={'p-4 hover:bg-slate-50 ' + (editandoResposta?.id === resp.id ? 'bg-blue-50 border-l-4 border-blue-500' : '')}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-800">{resp.titulo}</span>
                            <span className={'text-xs px-2 py-0.5 rounded-full ' + 
                              (resp.categoria === 'saudacao' ? 'bg-blue-100 text-blue-700' :
                               resp.categoria === 'encerramento' ? 'bg-purple-100 text-purple-700' :
                               resp.categoria === 'financeiro' ? 'bg-yellow-100 text-yellow-700' :
                               resp.categoria === 'suporte' ? 'bg-red-100 text-red-700' :
                               resp.categoria === 'comercial' ? 'bg-green-100 text-green-700' :
                               'bg-slate-100 text-slate-700')}>
                              {resp.categoria}
                            </span>
                            {editandoResposta?.id === resp.id && (
                              <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">
                                Editando
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 whitespace-pre-wrap">{resp.texto}</p>
                        </div>
                        <div className="flex gap-1">
                          {podeEditar && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                              onClick={function() { iniciarEdicaoResposta(resp); }}
                              title="Editar resposta"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {podeExcluir && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={function() { excluirRespostaRapida(resp.id); }}
                              title="Excluir resposta"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <strong>Dica:</strong> As respostas rápidas aparecem na página de Atendimentos. Use-as para agilizar suas respostas aos clientes!
          </div>
        </div>
      )}

      {activeTab === 'planos' && (
        <div className="space-y-6">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-orange-800 mb-4">
            <strong>Importante:</strong> Esses planos são exibidos no atendimento automático quando um cliente deseja contratar ou conhecer os serviços.
          </div>

          {/* Planos de Internet Fibra */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                🌐 Planos de Internet Fibra
              </h3>
              <Button size="sm" onClick={function() { adicionarPlano('fibra'); }}>
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2">
              {planosFibra.map(function(plano, index) {
                return (
                  <div key={plano.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <span className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <Input
                      value={plano.nome}
                      onChange={function(e) { atualizarPlano('fibra', plano.id, 'nome', e.target.value); }}
                      placeholder="Nome do plano (ex: 300 MEGA)"
                      className="flex-1"
                    />
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500">R$</span>
                      <Input
                        value={plano.valor}
                        onChange={function(e) { atualizarPlano('fibra', plano.id, 'valor', e.target.value); }}
                        placeholder="99,90"
                        className="w-24"
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700"
                      onClick={function() { removerPlano('fibra', plano.id); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
              {planosFibra.length === 0 && (
                <p className="text-slate-400 text-center py-4">Nenhum plano cadastrado</p>
              )}
            </div>
          </div>

          {/* Planos Móvel */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                📱 Planos Móvel
              </h3>
              <Button size="sm" onClick={function() { adicionarPlano('movel'); }}>
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2">
              {planosMovel.map(function(plano, index) {
                return (
                  <div key={plano.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <span className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <Input
                      value={plano.nome}
                      onChange={function(e) { atualizarPlano('movel', plano.id, 'nome', e.target.value); }}
                      placeholder="Nome do plano (ex: 30GB + Ligações)"
                      className="flex-1"
                    />
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500">R$</span>
                      <Input
                        value={plano.valor}
                        onChange={function(e) { atualizarPlano('movel', plano.id, 'valor', e.target.value); }}
                        placeholder="69,90"
                        className="w-24"
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700"
                      onClick={function() { removerPlano('movel', plano.id); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
              {planosMovel.length === 0 && (
                <p className="text-slate-400 text-center py-4">Nenhum plano cadastrado</p>
              )}
            </div>
          </div>

          {/* Planos TV */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                📺 Planos TV
              </h3>
              <Button size="sm" onClick={function() { adicionarPlano('tv'); }}>
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2">
              {planosTv.map(function(plano, index) {
                return (
                  <div key={plano.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                    <span className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <Input
                      value={plano.nome}
                      onChange={function(e) { atualizarPlano('tv', plano.id, 'nome', e.target.value); }}
                      placeholder="Nome do plano (ex: Premium 180 canais)"
                      className="flex-1"
                    />
                    <div className="flex items-center gap-1">
                      <span className="text-slate-500">R$</span>
                      <Input
                        value={plano.valor}
                        onChange={function(e) { atualizarPlano('tv', plano.id, 'valor', e.target.value); }}
                        placeholder="79,90"
                        className="w-24"
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700"
                      onClick={function() { removerPlano('tv', plano.id); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
              {planosTv.length === 0 && (
                <p className="text-slate-400 text-center py-4">Nenhum plano cadastrado</p>
              )}
            </div>
          </div>

          {/* Botão Salvar */}
          <div className="flex justify-end">
            <Button 
              onClick={salvarPlanos} 
              disabled={salvandoPlanos || !podeEditar}
              className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
              title={!podeEditar ? 'Você não tem permissão para editar' : ''}
            >
              {salvandoPlanos ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Planos
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Aviso de permissão */}
      {!podeVer && !isSuperAdmin && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-center">
          <p className="font-medium">Acesso Restrito</p>
          <p className="text-sm">Você não tem permissão para acessar o Construtor de Menu. Solicite acesso ao administrador.</p>
        </div>
      )}
    </div>
  );
}
